import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import "./Clients.scss";
import IDeleteClientDTO from "../../../models/DTO/IDeleteClientDTO";
import IGetClientsDTO from "../../../models/DTO/IGetClientsDTO";
import IOnCreateClientDTO from "../../../models/DTO/IOnCreateClientDTO";
import IOnDeleteClientDTO from "../../../models/DTO/IOnDeleteClientDTO";
import IOnGetClientsDTO from "../../../models/DTO/IOnGetClientsDTO";
import IOnUpdateClientDTO from "../../../models/DTO/IOnUpdateClientDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import IClient from "../../../models/IClient";
import IClientUI from "../../../models/IClientUI";
import IOwTableCols from "../../../models/IOwTableCols";
import IVendorModel from "../../../models/IVendorModel";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwGoogleStaticMap from "../../shared/OwGoogleStaticMap/OwGoogleStaticMap";
import OwTable from "../../shared/OwTable/OwTable";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import ClientSidebar from "../ClientSidebar/ClientSidebar";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import UIService from "../../../services/UIService";
import { TypeaheadFn } from "../../shared/TypeaheadFn/TypeaheadFn";
import IGetVendorsDTO from "../../../models/DTO/IGetVendorsDTO";
import IOnGetVendorsDTO from "../../../models/DTO/IOnGetVendorsDTO";
import AccessTypesEnum from "../../../models/AccessTypesEnum";
import {SearchSection} from "../../shared/SearchSection/SearchSection";

type Props = {};

export const Clients = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const onGetClients = (data: IOnGetClientsDTO) => {
        const _clients: IClientUI[] = data.clients.map((client: IClient) => {
            return {
                ...client,
                rowEditMode: false,
                isSaved: true,
                isDirty: false,
            } as IClientUI;
        });

        setTotalRecords(data.totalClients);
        setClients(_clients);
        setIsDataFetching(false);
        setIsInitialData(false);
        uiContext.setActivityIndicator(false);
    };

    const onCreateClient = (data: IOnCreateClientDTO) => {
        getClients();
    };

    const onUpdateClient = (data: IOnUpdateClientDTO) => {
        getClients();
    };

    const onDeleteClient = (data: IOnDeleteClientDTO) => {
        getClients();
    };

    const onGetVendors = (data: IOnGetVendorsDTO) => {
        setFoundVendors(data.supplierVendorTokens.map((token) => token.Vendor!));
    };

    const actionRenderFn = (row: IClientUI, index: number) => {
        return (
            <div className={"d-flex flex-row align-items-center"}>
                <span className={"state-circle me-4 " + (row.isSaved ? "bluish-green-bg" : "red-bg")} />

                {/* <Link to={`/client/${data.guid}`}><i className="far fa-eye"></i></Link> */}

                <span className="ms-4" />
                {userModel.supplierUserToken?.access_type_id === AccessTypesEnum.READ_ONLY ? null : (
                    <OwDeleteEditActions
                        isEditMode={row.rowEditMode}
                        onTrashClickFn={(e) => {
                            const rowElem = document.getElementById(`row-${index}`);
                            toggleConfirmDelete(e, row, index, rowElem);
                        }}
                        onEditClickFn={(e) => {
                            showSidebar(row);
                        }}
                    />
                )}
            </div>
        );
    };

    const billToAddressRenderFn = (client: IClientUI, index: number) => {
        if (!client.Bill_To) {
            return null;
        }

        return (
            <div className={"d-flex"}>
                {supplierModel.has_google_maps ? <OwGoogleStaticMap address={client.Bill_To} size={"120x120"} className={"me-2"} /> : null}
                <AddressDisplay address={client.Bill_To} showName={true} />
            </div>
        );
    };

    const addressRenderFn = (client: IClientUI, index: number) => {
        if (!client.Ship_To) {
            return null;
        }

        return (
            <div className={"d-flex"}>
                {supplierModel.has_google_maps ? <OwGoogleStaticMap address={client.Ship_To} size={"120x120"} className={"me-2"} /> : null}
                <AddressDisplay address={client.Ship_To} showName={true} />
            </div>
        );
    };

    const getTableColumns = (): IOwTableCols[] => {
        const _columns: IOwTableCols[] = [
            {
                isActionsCol: true,
                renderFn: actionRenderFn,
                title: "",
                valueProp: "",
            },
        ];

        if (userModel.isSupplierUser) {
            _columns.push({
                title: "vendor",
                valueProp: "Vendor",
                valueSubProp: "name",
                renderFnEmitsRow: true,
                titleRenderFn: () => {
                    return <OwLabel title={"vendor"} isSupplierVendorOverride={true} supplierModel={supplierModel} />;
                },
            });
        }

        _columns.push(
            {
                title: "name",
                valueProp: "name",
            },
            {
                title: "email",
                valueProp: "email",
            },
            {
                title: "bill_to",
                valueProp: "",
                renderFn: billToAddressRenderFn,
                renderFnEmitsRow: true,
            },
            {
                title: "ship_to",
                valueProp: "",
                renderFn: addressRenderFn,
                renderFnEmitsRow: true,
            }
        );

        return _columns;
    };

    const navItems: IBreadcrumbs[] = [
        { title: "Dashboard", url: "/dashboard" },
        { title: "Clients", url: "/clients" },
    ];

    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isInitialData, setIsInitialData] = useState(true);
    const [clients, setClients] = useState<IClientUI[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(20);
    const [name, setName] = useState("");
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [confirmDeleteElemId, setConfirmDeleteElemId] = useState<string | null>(null);
    const [columns, setColumns] = useState<IOwTableCols[]>(getTableColumns());
    const [foundVendors, setFoundVendors] = useState<IVendorModel[]>([]);
    const [selectedVendor, setSelectedVendor] = useState<IVendorModel | null>(null);
    const [resetSearch, setResetSearch] = useState(false);

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.ClientOnGetClients, fn: onGetClients },
        { endpoint: EndpointsEnum.ClientOnCreateClient, fn: onCreateClient },
        { endpoint: EndpointsEnum.ClientOnUpdateClient, fn: onUpdateClient },
        { endpoint: EndpointsEnum.ClientOnDeleteClient, fn: onDeleteClient },
        { endpoint: EndpointsEnum.VendorsOnGetVendors, fn: onGetVendors },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        document.title = `Orderwerks >> Clients`;

        getClients();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        if (resetSearch) {
            getClients();
            setResetSearch(false);
        }
    }, [resetSearch]);

    const cancelSidebar = () => {
        uiContext.toggleSidebar(false);

        getClients();
    };

    const showSidebar = (row?: IClientUI) => {
        const component = <ClientSidebar client={row} cancelFn={cancelSidebar} />;

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const getQueryString = (): string | undefined => {
        const queryStringArr: string[] = [];

        if (name?.length) {
            queryStringArr.push(`name=${name}`);
        }

        if (selectedVendor) {
            queryStringArr.push(`vendor=${selectedVendor.guid}`);
        }

        if (queryStringArr.length) {
            return `?` + queryStringArr.join("&");
        }
    };

    const getClients = () => {
        const data: IGetClientsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            limit,
            offset,
        };

        const queryString = getQueryString();

        if (queryString) {
            data.queryString = queryString;
        }

        socket.emit(EndpointsEnum.ClientGetClients, data);

        uiContext.setActivityIndicator(true);

        setIsDataFetching(true);
    };

    const toggleRowEditMode = (rowData: any, index: number) => {
        if (rowData.rowEditMode) {
            // check to see if isSaved or if isDirty.
            if (rowData.isDirty && rowData.guid) {
                // rowData.guid = rowData.guid;
                rowData.vendorGuid = vendorModel.guid;
                rowData.userToken = userModel.token;
                rowData.userGuid = userModel.guid;

                socket.emit("updateClient", rowData);
            } else {
                rowData.vendorGuid = vendorModel.guid;
                rowData.userToken = userModel.token;
                rowData.userGuid = userModel.guid;

                socket.emit("createClient", rowData);
            }

            // if cancel, then restore data to previous.
        }

        const updatedClients = [...clients];

        updatedClients[index].rowEditMode = !rowData.rowEditMode;

        setClients(updatedClients);
    };

    const createClientLocal = (event: any) => {
        const newClient = {
            name: "",
            address_line_one: "",
            address_line_two: "",
            city: "",
            state: "",
            postal_code: "",
            country: "",
            isSaved: false,
            isDirty: true,
            rowEditMode: true,
            // showDetailView: false,
        } as IClientUI;

        setClients([newClient, ...clients]);
    };

    const rowUpdate = (e: any, data: any, index: any, prop: any) => {
        const updatedClients = [...clients];
        // @ts-expect-error
        updatedClients[index][prop] = e.target.value;
        updatedClients[index].isDirty = true;

        setClients(updatedClients);
    };

    const toggleConfirmDelete = (e: any, data: IClientUI, index: number, rowElem: any) => {
        setDeleteIndex(index);
        setSelectedRow(rowElem);
        setConfirmDeleteElemId(`row-${index}`);
    };

    const commitDelete = () => {
        if (deleteIndex) {
            const client = clients[deleteIndex];

            if (client) {
                const payload: IDeleteClientDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    vendorGuid: vendorModel.guid,
                    clientGuid: client.guid,
                };

                socket.emit("deleteClient", payload);
            }

            setDeleteIndex(null);
            setSelectedRow(null);
            setConfirmDeleteElemId(null);
        }
    };

    const cancelDelete = () => {
        setDeleteIndex(null);
        setSelectedRow(null);
        setConfirmDeleteElemId(null);
    };

    const onPaginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const _name = e.target.name;
    };

    const search = () => {
        getClients();
    };

    const reset = () => {
        setName("");
        setSelectedVendor(null);
        setResetSearch(true);
    };

    const selectVendor = (vendor: IVendorModel) => {
        setSelectedVendor(vendor);
    };

    const getVendors = (vendorName: string) => {
        const payload: IGetVendorsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString: `name=${vendorName}`,
        };

        socket.emit(EndpointsEnum.VendorsGetVendors, payload);
    };

    return (
        <div className="Clients position-relative" id={"clients"}>

            <div className="row mt-2">
                <div className="col-12 d-flex align-items-center justify-items-center">
                    <OwTitle title="clients" leftIconName="fad fa-user-tie" />
                    {userModel?.supplierUserToken?.access_type_id === AccessTypesEnum.READ_ONLY ? null : (
                        <OwButton
                            fn={() => {
                                showSidebar();
                            }}
                            className="ms-2 btn-warning"
                            title="create"
                            leftIconClass="fas fa-plus"
                        />
                    )}
                </div>
            </div>

            <SearchSection>
                <div className={"d-flex row"}>
                    {userModel.isSupplierUser ? (
                        <div className={"col-3"}>
                            <div>
                                <OwLabel title={"vendor"} isSupplierVendorOverride={true} supplierModel={supplierModel} />
                            </div>
                            <TypeaheadFn
                                lookupFn={getVendors}
                                availableOptions={foundVendors}
                                titleProp={"name"}
                                valueProp={"guid"}
                                selectedOptionFn={selectVendor}
                                placeholder={"vendor"}
                            />
                        </div>
                    ) : null}
                    <div className={"col-3"}>
                        <div>
                            <OwLabel title={"name"} />
                        </div>
                        <OwInputWrapper name={"name"} value={name} fn={handleChange} />
                    </div>
                    <div className="col-2">
                        <div>
                            <OwLabel title={" "} skipTranslate={true} />
                        </div>
                        <OwButton title={"search"} fn={search} />
                        <OwButton title={"reset"} fn={reset} className={"btn-danger ms-2"} />
                    </div>
                </div>
            </SearchSection>

            <div className="mt-2 table-responsive position-relative">
                <ConfirmDeleteOverlayFn
                    targetElem={confirmDeleteElemId}
                    handleConfirm={commitDelete}
                    handleCancel={cancelDelete}
                    skipMainView={true}
                    nestedParentElem={'clients-table'}
                />

                <OwTable
                    id={"clients-table"}
                    columns={columns}
                    dataSet={clients}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>

            <div className={"mt-2"}>
                <PaginationBar offset={offset} limit={limit} totalRecords={totalRecords} onPaginationChanged={onPaginationChanged} />
            </div>
        </div>
    );
};
